'use client';

import Link from 'next/link';
import { Note } from '@ui/components';
import { ReactNode } from 'react';

export interface IBreadcrumbItem {
  title: string;
  href?: string;
  onClick?: () => void;
}

export interface IBreadcrumbs {
  items: IBreadcrumbItem[];
  className?: string;
  separator?: ReactNode;
  renderItem?: (item: IBreadcrumbItem, index: number, isLast: boolean) => ReactNode;
}

export function Breadcrumbs({ items, className = '', separator = '/', renderItem }: IBreadcrumbs) {
  if (items.length < 1) {
    return null;
  }
  const defaultRenderItem = (item: IBreadcrumbItem, index: number, isLast: boolean) => {
    return (
      <div
        key={index}
        className={`group text-sm flex items-center min-w-0 flex-shrink ${
          !isLast ? 'max-w-[25%] sm:max-w-[150px]' : 'flex-1 min-w-0'
        }`}>
        {item.href && !isLast ? (
          <Link
            className="group-last-of-type:cursor-default min-w-0 w-full overflow-hidden block"
            href={item.href}
            onClick={item.onClick}>
            <div title={item.title} className="min-w-0 w-full overflow-hidden">
              <Note type="s" className="group-last-of-type:text-subtitle block truncate">
                {item.title}
              </Note>
            </div>
          </Link>
        ) : (
          <div
            className="group-last-of-type:cursor-default min-w-0 w-full overflow-hidden block"
            onClick={item.onClick}>
            <div title={item.title} className="min-w-0 w-full overflow-hidden">
              <Note
                type="s"
                className="font-semibold group-last-of-type:text-subtitle block truncate">
                {item.title}
              </Note>
            </div>
          </div>
        )}
        {index !== items.length - 1 && <span className="mx-1 flex-shrink-0">{separator}</span>}
      </div>
    );
  };

  return (
    <div className={`flex min-w-0 w-full max-w-full overflow-hidden ${className}`}>
      {items.map((item, index) =>
        renderItem
          ? renderItem(item, index, index === items.length - 1)
          : defaultRenderItem(item, index, index === items.length - 1),
      )}
    </div>
  );
}
